import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, ChevronDown, Menu, X, User, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import CurrencySwitcher from "./CurrencySwitcher";

const SERVICES_DROPDOWN = [
  { to: "/services#website-design", label: "Website Design" },
  { to: "/services#seo", label: "SEO Optimization" },
  { to: "/services#api-integration", label: "API Integration" },
  { to: "/services#automation", label: "Business Automation" },
  { to: "/ai-builder", label: "AI Website Builder" },
  { to: "/ai-app-builder", label: "AI App Builder" },
];

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services", dropdown: SERVICES_DROPDOWN },
  { to: "/pricing", label: "Pricing" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, cart, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // Dark hero pages (Landing) — Navbar floats transparent over the hero,
  // text flips to white. All other routes keep the light navbar.
  const onDarkHero = loc.pathname === "/";
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const acctRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const onLogout = () => { logout(); setAcctOpen(false); nav("/"); };

  return (
    <header className={`sticky top-0 z-50 ${onDarkHero ? "header-dark" : "header-light"}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img
            src={onDarkHero ? "/optivize-logo-white.png" : "/optivize-logo.png"}
            alt="Optivize — DBA of Surajit Majhi HUF"
            className="h-16 sm:h-20 lg:h-24 w-auto object-contain drop-shadow-sm"
            style={{maxWidth: "360px"}}
            data-testid="logo-img"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            l.dropdown ? (
              <div key={l.to} className="relative" onMouseEnter={()=>setSvcOpen(true)} onMouseLeave={()=>setSvcOpen(false)}>
                <button
                  className={`flex items-center gap-1 text-[15px] font-medium transition-colors ${onDarkHero ? "text-slate-200 hover:text-blue-300" : "text-slate-800 hover:text-blue-600"}`}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label} <ChevronDown size={14}/>
                </button>
                {svcOpen && (
                  <div className="absolute top-full left-0 pt-2 w-56">
                    <div className="bg-white shadow-xl rounded-lg border border-slate-100 py-2">
                      {l.dropdown.map((d) => (
                        <Link key={d.to} to={d.to} className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600">
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[15px] font-medium transition-colors ${
                    isActive ? (onDarkHero ? "text-blue-300" : "text-blue-600") :
                               (onDarkHero ? "text-slate-200 hover:text-blue-300" : "text-slate-800 hover:text-blue-600")
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <CurrencySwitcher light />
          </div>
          <Link to="/checkout" className="relative text-slate-700 hover:text-blue-600 transition-colors" data-testid="cart-button">
            <ShoppingCart size={20} />
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 text-[10px] bg-blue-600 text-white font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">
                {cart.length}
              </span>
            )}
          </Link>

          {/* My Account dropdown */}
          <div ref={acctRef} className="relative">
            <button
              onClick={() => setAcctOpen(!acctOpen)}
              className="hidden md:inline-flex items-center gap-1 text-[15px] font-medium text-slate-800 hover:text-blue-600 transition-colors"
              data-testid="my-account-button"
            >
              <User size={16}/> My Account <ChevronDown size={14}/>
            </button>
            {acctOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white shadow-xl rounded-lg border border-slate-100 py-2 z-50" data-testid="my-account-menu">
                {user ? (
                  <>
                    <div className="px-4 py-2 border-b border-slate-100">
                      <div className="text-sm text-slate-800 font-semibold truncate">{user.name}</div>
                      <div className="text-xs text-slate-500 truncate">{user.email}</div>
                    </div>
                    {user.role === "admin" ? (
                      <Link to="/admin" onClick={()=>setAcctOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-800 hover:bg-slate-50" data-testid="acct-admin-link">
                        <ShieldCheck size={14} className="text-amber-500"/> Admin Portal
                      </Link>
                    ) : (
                      <Link to="/dashboard" onClick={()=>setAcctOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-800 hover:bg-slate-50" data-testid="acct-dashboard-link">
                        <LayoutDashboard size={14} className="text-blue-500"/> Customer Dashboard
                      </Link>
                    )}
                    <button onClick={onLogout} className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50" data-testid="acct-logout-btn">
                      <LogOut size={14}/> Sign out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={()=>setAcctOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-800 hover:bg-slate-50" data-testid="acct-login-link">
                      <User size={14}/> Customer Login
                    </Link>
                    <Link to="/login" onClick={()=>setAcctOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-800 hover:bg-slate-50" data-testid="acct-admin-login-link">
                      <ShieldCheck size={14} className="text-amber-500"/> Admin Login
                    </Link>
                    <div className="border-t border-slate-100 my-1"/>
                    <Link to="/signup" onClick={()=>setAcctOpen(false)} className="block px-4 py-2 text-sm text-blue-600 font-semibold hover:bg-blue-50" data-testid="acct-signup-link">
                      Create an account
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>

          <Link
            to={user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/signup"}
            className="btn-primary !py-2.5 !px-5 text-sm hidden md:inline-flex"
            data-testid="get-started-btn"
          >
            {user ? (user.role === "admin" ? "Admin" : "Dashboard") : "Get Started"}
          </Link>
          <button className="lg:hidden text-slate-800" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-5 py-3 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm font-medium ${isActive ? "text-blue-600 bg-blue-50" : "text-slate-800 hover:bg-slate-100"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to={user ? "/dashboard" : "/signup"} onClick={()=>setOpen(false)} className="btn-primary !py-2.5 !px-5 text-sm mt-2 justify-center">
              {user ? "Dashboard" : "Get Started"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
